import axios from 'axios';

// Use localhost as default for development if no env var is provided
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
export const BASE_URL = API_BASE_URL;
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Payment APIs
export const paymentApi = {
  createOrder: (data) =>
    apiClient.post('/payments/create-order', data, { withCredentials: false }),
  captureOrder: (token) => apiClient.post(`/payments/capture-order/${token}`),
};

// User Auth APIs
export const userAuthApi = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  logout: () => apiClient.post('/auth/logout', {}),
  getCurrentUser: () => apiClient.get('/auth/me'),
  getMyPayments: () => apiClient.get('/auth/my-payments'),
  getMyInvoices: () => apiClient.get('/auth/my-invoices'),
  getInvoice: (invoiceNumber) => apiClient.get(`/auth/invoice/${encodeURIComponent(invoiceNumber)}`),
};

// Admin Auth APIs
export const adminAuthApi = {
  setupAdmin: (data) => apiClient.post('/admin-auth/setup-admin', data),
  login: (data) => apiClient.post('/admin-auth/login', data),
  forgotPassword: (data) => apiClient.post('/admin-auth/forgot-password', data),
  resetPassword: (data) => apiClient.post('/admin-auth/reset-password', data),
  logout: () => apiClient.post('/admin-auth/logout', {}),
  getCurrentUser: () => apiClient.get('/admin-auth/me'),
  changePassword: (data) => apiClient.post('/admin-auth/change-password', data),
  updateProfile: (data) => apiClient.patch('/admin-auth/profile', data),
  createAdmin: (data) => apiClient.post('/admin-auth/create-admin', data),
  listAdmins: () => apiClient.get('/admin-auth/admins'),
  removeAdmin: (id) => apiClient.delete(`/admin-auth/admins/${id}`),
  refresh: () => apiClient.post('/admin-auth/refresh', {}),
  setupTwoFactor: () => apiClient.post('/admin-auth/2fa/setup', {}),
  verifyTwoFactor: (otpCode) => apiClient.post('/admin-auth/2fa/verify', { otpCode }),
};

// Admin APIs
export const adminApi = {
  getSummary: (filters) => apiClient.get('/admin/summary', { params: filters }),
  getPayments: (filters) => apiClient.get('/admin/payments', { params: filters }),
  getAuditLogs: (filters) => apiClient.get('/admin/audit-logs', { params: filters }),
  exportAuditLogs: (filters) => apiClient.get('/admin/audit-logs/export', {
    params: filters,
    responseType: 'blob'
  }),
  getUsers: (filters) => apiClient.get('/admin/users', { params: filters }),
  createInvoice: (data) => apiClient.post('/admin/invoices', data),
  getInvoices: (filters) => apiClient.get('/admin/invoices', { params: filters }),
  cancelInvoice: (id) => apiClient.post(`/admin/invoices/${id}/cancel`, {}),
  updatePaymentStatus: (paymentId, data) => apiClient.post(`/admin/payments/${paymentId}/status`, data),
  approvePayment: (paymentId) => apiClient.post(`/admin/payments/${paymentId}/approve`),
  getCompanies: (filters) => apiClient.get('/admin/companies', { params: filters }),
  createCompany: (data) => apiClient.post('/admin/companies', data),
  updateCompany: (id, data) => apiClient.patch(`/admin/companies/${id}`, data),
  deleteCompany: (id) => apiClient.delete(`/admin/companies/${id}`),
};

// CSE Market Data APIs (admin)
// `dateParams` is either { date } for a single trading day, or
// { startDate, endDate } for a range — both come straight from the page's
// date picker state and are passed through as query params as-is.
export const cseMarketApi = {
  getTradeSummary: (dateParams) => apiClient.get('/admin/cse-market/trade-summary', { params: dateParams }),
  getDetailedTrades: (board, dateParams) => apiClient.get('/admin/cse-market/detailed-trades', { params: { board, ...dateParams } }),
  getGicsSummary: (dateParams) => apiClient.get('/admin/cse-market/gics-summary', { params: dateParams }),
  getMarketCap: (dateParams) => apiClient.get('/admin/cse-market/market-cap', { params: dateParams }),
  getAvailableDates: () => apiClient.get('/admin/cse-market/available-dates'),
  getSymbols: () => apiClient.get('/admin/cse-market/symbols'),
  getStatus: () => apiClient.get('/admin/cse-market/status'),
  fetchNow: () => apiClient.post('/admin/cse-market/fetch-now', {}),
};

// AI Document Analysis API (admin) — upload a PDF/XLS/CSV/image and/or ask a question
export const aiApi = {
  analyze: (formData) => apiClient.post('/admin/ai/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Health check
export const healthCheck = () => apiClient.get('/health');

// Export axios client for advanced cases
export default apiClient;
